import { formatCurrencyID, formatUnitPriceInput, parseUnitPrice } from '../../utils/currency';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { CalendarIcon, Lock, Package } from 'lucide-react';
import { Calendar } from '../ui/calendar';

export function POPartialDialog({ allItemsReceived, handleConfirmPartial, handlePartialItemChange, hasAdjustments, partialItems, selectedPO, setShowPartial, setTanggalTerima, showPartial, tanggalTerima }) {
  return (
    <Dialog open={showPartial} onOpenChange={setShowPartial}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-600" />
            Partial Delivery - Centang Item yang Diterima
          </DialogTitle>
        </DialogHeader>

        {selectedPO && (
          <div className="space-y-4">
            <div className="bg-orange-50 p-3 rounded-lg">
              <p className="text-sm"><strong>No. PO:</strong> {selectedPO.po_number}</p>
              <p className="text-sm"><strong>Supplier:</strong> {selectedPO.supplier_name}</p>
              <p className="text-sm"><strong>Jenis Pengiriman:</strong> Partial (Bertahap)</p>
            </div>

            <div className="space-y-2">
              <Label>Tanggal Terima Barang *</Label>
              <Popover modal={false}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    disabled={partialItems.length > 0 && partialItems.every(it => it.locked)}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {tanggalTerima ? format(tanggalTerima, 'dd MMMM yyyy', { locale: id }) : 'Pilih tanggal'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start">
                  <Calendar
                    mode="single"
                    selected={tanggalTerima}
                    onSelect={setTanggalTerima}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {partialItems.length > 0 && partialItems.every(it => it.locked) && (
                <p className="text-xs text-slate-600 italic flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Semua item sudah lunas — TTB terkunci, tidak ada input yang dapat diubah.
                </p>
              )}
            </div>

            {/* Info: All items checked = complete */}
            {allItemsReceived() && (
              <div className={`p-3 rounded-lg border ${hasAdjustments() ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm font-semibold ${hasAdjustments() ? 'text-yellow-700' : 'text-green-700'}`}>
                  {hasAdjustments() 
                    ? 'Semua item dicentang dengan adjustment → Status akan menjadi "Diterima (Adj)"'
                    : 'Semua item dicentang sesuai PO → Status akan menjadi "Diterima"'}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Label>Centang item yang diterima (bisa edit qty & harga):</Label>
              <div className="border rounded-lg divide-y">
                {partialItems.map((item, index) => (
                  <div
                    key={item.line_id || index}
                    className={`p-3 space-y-2 ${item.locked ? 'bg-slate-100 opacity-90' : (item.received ? 'bg-green-50' : '')}`}
                    data-testid={`partial-row-${item.line_id || index}`}
                  >
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={item.received}
                        disabled={item.locked}
                        onCheckedChange={(checked) => handlePartialItemChange(index, 'received', checked)}
                        data-testid={`partial-item-${index}`}
                      />
                      <div className="flex-1">
                        <p className="font-semibold flex items-center gap-2">
                          {item.name}
                          {item.locked && (
                            <span
                              className="inline-flex items-center gap-1 text-xs bg-slate-700 text-white px-2 py-0.5 rounded-full font-normal"
                              data-testid={`locked-badge-${item.line_id || index}`}
                            >
                              <Lock className="h-3 w-3" /> Terkunci (TTB)
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-600">
                          PO: {item.original_qty} {item.satuan} x Rp {(item.original_price || 0).toLocaleString('id-ID')}
                        </p>
                      </div>
                      <div className="text-right min-w-[100px]">
                        <p className="font-mono font-semibold">
                          Rp {((item.received ? item.received_qty : 0) * (item.received_price || item.unit_price || 0)).toLocaleString('id-ID')}
                        </p>
                      </div>
                    </div>
                    {item.received && (
                      <div className="flex items-center gap-4 pl-8">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">Qty Terima:</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.received_qty}
                            onChange={(e) => handlePartialItemChange(index, 'received_qty', parseInt(e.target.value) || 1)}
                            disabled={item.locked}
                            className="w-20"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">Harga (Rp):</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={item.received_price_display !== undefined ? item.received_price_display : formatCurrencyID(item.received_price || item.unit_price)}
                            onChange={(e) => {
                              const displayValue = formatUnitPriceInput(e.target.value);
                              handlePartialItemChange(index, 'received_price_display', displayValue);
                              handlePartialItemChange(index, 'received_price', parseUnitPrice(displayValue));
                            }}
                            disabled={item.locked}
                            className="w-28"
                          />
                        </div>
                        {item.locked && (
                          <span className="text-xs text-slate-500 italic">Sudah lunas — tidak dapat diubah</span>
                        )}
                        {!item.locked && (item.received_qty !== item.original_qty || (item.received_price || item.unit_price) !== item.original_price) && (
                          <span className="text-xs text-yellow-600 font-semibold">* Diubah</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
              <div className="flex justify-between font-bold">
                <span>Total Diterima</span>
                <span className="font-mono text-orange-600">
                  Rp {partialItems
                    .filter(item => item.received)
                    .reduce((sum, item) => sum + ((item.received_qty || 0) * (item.received_price || item.unit_price || 0)), 0)
                    .toLocaleString('id-ID')}
                </span>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowPartial(false)}>Batal</Button>
              <Button 
                className={allItemsReceived() ? (hasAdjustments() ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700') : 'bg-orange-500 hover:bg-orange-600'}
                onClick={handleConfirmPartial}
                disabled={
                  !partialItems.some(item => item.received)
                  || partialItems.every(item => item.locked)
                }
                data-testid="partial-save-btn"
              >
                Simpan & Cetak TTB
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
